

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { authService } from '../services/auth';
import { USER_ROLES } from '../utils/constants';
import { decodeToken, isTokenExpired, isValidTokenFormat,getToken,setToken,clearTokens } from '../utils/auth';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Safe localStorage wrapper
const safeStorage = {
  getItem: (key) => {
    try {
      const value = localStorage.getItem(key);
      console.log(`📝 [STORAGE] GET ${key}:`, value ? `${value.length} chars` : 'null');
      return value;
    } catch (error) {
      console.error(`❌ [STORAGE] Error getting ${key}:`, error);
      return null;
    }
  },
  
  setItem: (key, value) => {
    try {
      console.log(`📝 [STORAGE] SET ${key}:`);
      console.log(`   Value: ${value}`);
      console.log(`   Type: ${typeof value}`);
      console.log(`   Length: ${value?.length}`);
      
      if (value === undefined) {
        console.error(`❌ [STORAGE] Cannot store undefined for ${key}`);
        return false;
      }
      
      if (value === 'undefined') {
        console.error(`❌ [STORAGE] Cannot store string "undefined" for ${key}`);
        return false;
      }
      
      localStorage.setItem(key, value);
      console.log(`✅ [STORAGE] ${key} stored successfully`);
      return true;
    } catch (error) {
      console.error(`❌ [STORAGE] Error setting ${key}:`, error);
      return false;
    }
  },
  
  removeItem: (key) => {
    try {
      console.log(`🗑️ [STORAGE] REMOVE ${key}`);
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`❌ [STORAGE] Error removing ${key}:`, error);
      return false;
    }
  },
  
  clear: () => {
    try {
      console.log('🧹 [STORAGE] CLEAR ALL');
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('❌ [STORAGE] Error clearing:', error);
      return false;
    }
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const initializing = useRef(false);

  useEffect(() => {
    if (!initializing.current) {
      initializing.current = true;
      console.log('🔄 [AUTH CONTEXT] Initializing auth...');
      initializeAuth();
    }
  }, []);

  const initializeAuth = () => {
  const token = getToken(); // now reads sessionStorage
  const storedUser = sessionStorage.getItem('user');

    console.log('📋 [AUTH CONTEXT] Stored data:');
    console.log('   Token:', token);
    console.log('   User:', storedUser);

    if (token && storedUser) {
      try {
        // Check for "undefined" string
        if (token === 'undefined') {
          console.error('❌ [AUTH CONTEXT] Token is string "undefined"');
          safeStorage.removeItem('token');
          safeStorage.removeItem('user');
          setAuthError('Invalid session - please login again');
          setLoading(false);
          return;
        }

        // Validate token
        if (!isValidTokenFormat(token)) {
          console.error('❌ [AUTH CONTEXT] Invalid token format');
          safeStorage.removeItem('token');
          safeStorage.removeItem('user');
          setAuthError('Session invalid - please login again');
          setLoading(false);
          return;
        }

        // Check expiration
        if (isTokenExpired(token)) {
          console.warn('⚠️ [AUTH CONTEXT] Token expired');
          safeStorage.removeItem('token');
          safeStorage.removeItem('user');
          setAuthError('Your session has expired');
          setLoading(false);
          return;
        }

        const userData = JSON.parse(storedUser);
        console.log('✅ [AUTH CONTEXT] Valid user restored:', userData);
        setUser(userData);
        setAuthError(null);
        
      } catch (error) {
        console.error('❌ [AUTH CONTEXT] Initialization error:', error);
        safeStorage.clear();
        setAuthError('Failed to restore session');
      }
    } else {
      console.log('👤 [AUTH CONTEXT] No stored session found');
    }

    setLoading(false);
    initializing.current = false;
  };

  const login = async (email, password) => {
    console.log('🔐 [AUTH CONTEXT] Admin login for:', email);
    setAuthError(null);
    
    try {
      const result = await authService.login(email, password);

      if (!result.success) {
        console.error('❌ [AUTH CONTEXT] Admin login failed:', result.error);
        setAuthError(result.error);
        return result;
      }

      const { token, ...userData } = result.user;

      console.log('📋 [AUTH CONTEXT] Login result:');
      console.log('   Token:', token);
      console.log('   Token length:', token?.length);
      console.log('   User data:', userData);

      // Validate token
      if (!token || token === 'undefined') {
        const errorMsg = `Invalid token: ${token}`;
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      if (!isValidTokenFormat(token)) {
        const errorMsg = 'Token has invalid format';
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      // Store securely
      // if (!safeStorage.setItem('token', token)) {
      //   const errorMsg = 'Failed to store authentication';
      //   console.error('❌ [AUTH CONTEXT]', errorMsg);
      //   setAuthError(errorMsg);
      //   return {
      //     success: false,
      //     error: errorMsg,
      //   };
      // }
      // Store securely

      setToken(token, 'admin');
sessionStorage.setItem('user', JSON.stringify(userData));

      if (!safeStorage.setItem('user', JSON.stringify(userData))) {
        safeStorage.removeItem('token');
        const errorMsg = 'Failed to store user data';
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      setUser(userData);
      setAuthError(null);
      
      console.log('✅ [AUTH CONTEXT] Admin login complete');
      
      // FIXED: Return user WITH token
      return { 
        success: true, 
        user: { 
          ...userData, 
          token // Include the token!
        } 
      };
    } catch (error) {
      console.error('❌ [AUTH CONTEXT] Login error:', error);
      const errorMsg = error.message || 'Login failed';
      setAuthError(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    }
  };

  const businessLogin = async (username, password) => {
    setAuthError(null);
    
    try {
      const result = await authService.businessLogin(username, password);

      if (!result.success) {
        console.error('❌ [AUTH CONTEXT] Business login failed:', result.error);
        setAuthError(result.error);
        return result;
      }

      // If password change required
      if (result.requiresPasswordChange) {
        console.log('🔄 [AUTH CONTEXT] Password change required');
        
        if (!result.token || result.token === 'undefined') {
          console.error('❌ [AUTH CONTEXT] No temp token for password change');
          setAuthError('No temporary token received for password change');
          return {
            success: false,
            error: 'No temporary token received',
          };
        }
        
        return {
          success: true,
          requiresPasswordChange: true,
          business: result.business,
          token: result.token,
        };
      }

      // const token = result.token;
      // const userData = result.user;
      const token = result.token;
const decoded = decodeToken(token);
const userData = {
  ...result.user,
  // businesses._id — login returns `business` (not `user`), so take it from the token
  id: result.user?.id || decoded?.id || result.business?.id,
  businessId: decoded?.businessId || result.business?.businessId || result.user?.businessId,
};

  console.log('📋 [AUTH CONTEXT] Regular business login:');

  // Validate token
  if (!token || token === 'undefined') {
    const errorMsg = 'Invalid business token';
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      if (!isValidTokenFormat(token)) {
        const errorMsg = 'Business token has invalid format';
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      // // Store securely
      // if (!safeStorage.setItem('token', token)) {
      //   const errorMsg = 'Failed to store business authentication';
      //   console.error('❌ [AUTH CONTEXT]', errorMsg);
      //   setAuthError(errorMsg);
      //   return {
      //     success: false,
      //     error: errorMsg,
      //   };
      // }
      // Store securely
      setToken(token, 'business');
      sessionStorage.setItem('user', JSON.stringify(userData));


      if (!safeStorage.setItem('user', JSON.stringify(userData))) {
        safeStorage.removeItem('token');
        const errorMsg = 'Failed to store business user data';
        console.error('❌ [AUTH CONTEXT]', errorMsg);
        setAuthError(errorMsg);
        return {
          success: false,
          error: errorMsg,
        };
      }

      setUser(userData);
      setAuthError(null);
      
      console.log('✅ [AUTH CONTEXT] Business login complete');
      
      // FIXED: Return user WITH token
      return { 
        success: true, 
        user: { 
          ...userData, 
          token // Include the token!
        },
        requiresPasswordChange: false 
      };
    } catch (error) {
      console.error('❌ [AUTH CONTEXT] Business login error:', error);
      const errorMsg = error.message || 'Business login failed';
      setAuthError(errorMsg);
      return {
        success: false,
        error: errorMsg,
      };
    }
  };

  // const logout = () => {
  //   console.log('🚪 [AUTH CONTEXT] Logging out...');
  //   safeStorage.clear();
  //   setUser(null);
  //   setAuthError(null);
  // };

  const logout = () => {
  console.log('🚪 [AUTH CONTEXT] Logging out...');
  clearTokens();
  sessionStorage.removeItem('user');
  setUser(null);
  setAuthError(null);
};

  const updateUser = (userData) => {
    console.log('📝 [AUTH CONTEXT] Updating user:', userData);
    try {
      setUser(userData);
      safeStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('❌ [AUTH CONTEXT] Error updating user:', error);
    }
  };

  const updateToken = (newToken) => {
    console.log('🔄 [AUTH CONTEXT] Updating token, length:', newToken?.length || 0);
    try {
      if (newToken && isValidTokenFormat(newToken)) {
        safeStorage.setItem('token', newToken);
      } else {
        console.error('❌ [AUTH CONTEXT] Cannot update with invalid token');
      }
    } catch (error) {
      console.error('❌ [AUTH CONTEXT] Error updating token:', error);
    }
  };

  const isAuthenticated = () => {
    // const token = safeStorage.getItem('token');
    const token = getToken();
    if (!token || token === 'undefined') {
      console.log('🔐 [AUTH CONTEXT] No valid token');
      return false;
    }
    
    if (!isValidTokenFormat(token)) {
      console.log('🔐 [AUTH CONTEXT] Invalid token format');
      return false;
    }
    
    return !isTokenExpired(token);
  };

  const getCurrentUser = () => {
    return user;
  };

  const getCurrentRole = () => {
    if (!user) return null;
    
    if (user.role) {
      return user.role;
    }
    
    if (user.businessId) {
      return USER_ROLES.BUSINESS_USER;
    }
    
    return null;
  };

  const clearAuthError = () => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authError,
        login,
        businessLogin,
        logout,
        updateUser,
        updateToken,
        clearAuthError,
        isAuthenticated: isAuthenticated(),
        isAdmin: getCurrentRole() === USER_ROLES.ADMIN,
        isBusiness: getCurrentRole() === USER_ROLES.BUSINESS_USER,
        currentRole: getCurrentRole(),
        getCurrentUser,
      }}
    >
      {/* {!loading && children} */}
      {children}
    </AuthContext.Provider>
  );
};